import { Injectable } from '@angular/core';
import 'babel-polyfill';
import Pushy from 'pushy-sdk-web';
import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { environment } from "../../environments/environment";
import { SupabaseService } from "./supabase.service";
import { AuthService } from "./auth.service";
import { pairwise, startWith, tap } from "rxjs/operators";
import {
    ConfirmationDialog
} from "../../../../common/src/lib/modals/confirmation-dialog/confirmation-dialog.component";
import { ModalsService } from "../../../../common/src/lib/services/modals.service";
import { NotificationService } from "./notification.service";

declare let window: any;

interface PushyNotificationPayload {
  message: string;
  title?: string;
  url?: string;
  notificationId?: number;
  data?: {
    viewAs?: number[];
    fromUser?: number;
  };
}

const TOKEN_KEY = 'PUSHY_TOKEN';
const DONT_SHOW_AGAIN_KEY = 'DONT_SHOW_NOTIFICATIONS_PROMPT';

@Injectable({
  providedIn: 'root'
})
export class PushyService {

    constructor(
        private supabaseService: SupabaseService,
        private modalsService: ModalsService,
        private notificationService: NotificationService,
        private window: Window,
        private authService: AuthService,
    ) {
        this.authService.user$
            .pipe(
                startWith(null),
                pairwise(),
            )
            .subscribe(async ([prev, curr]) => {
                // if(environment.production) {
                    if(prev === null && curr !== null) {
                        const [isRegistered, canShowPrompt] = await Promise.all([
                            this.isRegistered(),
                            this.canShowPrompt()
                        ]);

                        if (!isRegistered && canShowPrompt) {
                            this.modalsService.open(ConfirmationDialog, {
                                data: {
                                    title: 'Enable notifications',
                                    message: 'Would you like to enable notifications?',
                                    actionTitle: 'Enable',
                                    actionColor: 'primary',
                                    cancelTitle: 'Cancel',
                                    action: () => this.register(),
                                    cancelAction: () => this.dontShowAgain()
                                }
                            });
                        } else if (isRegistered) {
                            // Set up notification CLICK listener for background/tap handling
                            this.window.Pushy!.setNotificationClickListener((data: PushyNotificationPayload) => {
                                this.handleNativeNotification(data);
                            });
                            this.window.Pushy!.listen();
                        }
                    } else if(prev !== null && curr === null) {
                        await this.unregister();
                        await this.unsetDontShowAgain();
                    }
                // }
        });
    }

    async register() {
        try {
            let token: string;
            if (Capacitor.isNativePlatform()) {
                // Use native Pushy SDK
                token = await this.registerNative();
            } else {
                // Use web Pushy SDK
                token = await Pushy.register({ appId: environment.pushy.appId });
            }
            
            await this.storeToken(token);
            this.supabaseService.rpcFunc('app_data', 'add_notification_token', { token });
        } catch (e: any) {
            console.error('Registration failed:', e);
        }
    }

    private registerNative(): Promise<string> {
        return new Promise((resolve, reject) => {
            if (window.Pushy) {
                window.Pushy.register((err: any, token: string) => {
                    if (err) {
                        reject(err);
                    } else {
                        resolve(token);
                        window.Pushy.listen();

                    }
                });
            } else {
                reject(new Error('Native Pushy plugin not found'));
            }
        });
    }

    private handleNativeNotification(data: PushyNotificationPayload): void {
        // If there's an action URL, navigate to it
        if (data.url) {
            this.notificationService.navigateFromNotification(data.url, data.data);
            
            // Mark notification as read if it has an ID
            console.log('=======', data.notificationId)
            if (data.notificationId) {
                this.notificationService.markAsRead(data.notificationId);
            }
        }
    }

    // private handleComplexNavigation(actionUrl: string, data: any): void {
    //     console.log('========= handle complex');
    //     // Store the navigation data for the notifications service to handle
    //     // This approach allows us to reuse the existing navigation logic
    //     const navigationEvent = {
    //         actionUrl,
    //         data,
    //         source: 'native-push'
    //     };
    //
    //     // Store in session storage for immediate pickup by the notifications component
    //     sessionStorage.setItem('pending-native-navigation', JSON.stringify(navigationEvent));
    //
    //     // Navigate to lobby or current route to trigger the navigation handling
    //     this.router.navigateByUrl(actionUrl).catch(error => {
    //         console.error('Failed to navigate from complex native notification:', error);
    //     });
    // }

    // private checkForNotificationOnResume(): void {
    //     console.log('========= Checking for notification on resume');
    //
    //     // Small delay to ensure native side has time to set any notification data
    //     setTimeout(() => {
    //         if (window.Pushy && window.Pushy.getStartupNotification) {
    //             const notification = window.Pushy.getStartupNotification();
    //             console.log('========= Notification on resume:', notification);
    //
    //             if (notification) {
    //                 console.log('========= Found notification data on resume, processing...');
    //                 this.handleNativeNotification(notification);
    //
    //                 // Clear the notification after handling
    //                 if (window.Pushy.clearStartupNotification) {
    //                     window.Pushy.clearStartupNotification();
    //                 }
    //             }
    //         }
    //     }, 500);
    // }

    async isRegistered(): Promise<boolean> {
        try {
            const token = await this.getToken();
            return token !== null;
        } catch (e) {
            return false;
        }
    }

    async canShowPrompt(): Promise<boolean> {
        const dontShow = await this.getDontShowAgain();
        return dontShow !== 'TRUE';
    }

    async dontShowAgain(): Promise<void> {
        await this.storeDontShowAgain('TRUE');
    }

    async unsetDontShowAgain(): Promise<void> {
        await this.removeDontShowAgain();
    }

    async unregister() {
        const token = await this.getToken();
        await this.removeToken();
        if(token) {
            return this.supabaseService.rpcFunc('app_data', 'delete_notification_token', { token });
        }
    }

    // Public method to check for pending native navigation
    checkPendingNativeNavigation(): any {
        const stored = sessionStorage.getItem('pending-native-navigation');
        if (stored) {
            sessionStorage.removeItem('pending-native-navigation');
            try {
                return JSON.parse(stored);
            } catch (error) {
                console.error('Failed to parse pending navigation:', error);
            }
        }
        return null;
    }

    // Debug method to manually check for notification data
    debugCheckNotification(): void {
        if (window.Pushy) {
            // Try different possible methods
            if (window.Pushy.getStartupNotification) {
                const startup = window.Pushy.getStartupNotification();
                console.log('========= getStartupNotification():', startup);
            }
            
            if (window.Pushy.getLastNotification) {
                const last = window.Pushy.getLastNotification();
                console.log('========= getLastNotification():', last);
            }
            
            if (window.Pushy.getNotification) {
                const notif = window.Pushy.getNotification();
                console.log('========= getNotification():', notif);
            }
        }
    }

    // Storage helper methods that work for both web and native
    private async storeToken(token: string): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            await Preferences.set({ key: TOKEN_KEY, value: token });
        } else {
            localStorage.setItem(TOKEN_KEY, token);
        }
    }

    private async getToken(): Promise<string | null> {
        if (Capacitor.isNativePlatform()) {
            const { value } = await Preferences.get({ key: TOKEN_KEY });
            return value;
        } else {
            return localStorage.getItem(TOKEN_KEY);
        }
    }

    private async removeToken(): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            await Preferences.remove({ key: TOKEN_KEY });
        } else {
            localStorage.removeItem(TOKEN_KEY);
        }
    }

    private async storeDontShowAgain(value: string): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            await Preferences.set({ key: DONT_SHOW_AGAIN_KEY, value });
        } else {
            localStorage.setItem(DONT_SHOW_AGAIN_KEY, value);
        }
    }

    private async getDontShowAgain(): Promise<string | null> {
        if (Capacitor.isNativePlatform()) {
            const { value } = await Preferences.get({ key: DONT_SHOW_AGAIN_KEY });
            return value;
        } else {
            return localStorage.getItem(DONT_SHOW_AGAIN_KEY);
        }
    }

    private async removeDontShowAgain(): Promise<void> {
        if (Capacitor.isNativePlatform()) {
            await Preferences.remove({ key: DONT_SHOW_AGAIN_KEY });
        } else {
            localStorage.removeItem(DONT_SHOW_AGAIN_KEY);
        }
    }
}
